/**
 * Keeps the locally cached app-lock policy in step with the workspace's.
 *
 * Headless, and mounted INSIDE the authenticated tree — the endpoint needs a
 * session, so it cannot be read from the lock provider, which by design runs
 * before one exists. The cache it writes is what makes the lock work offline
 * and decidable before the first paint on the next launch.
 *
 * There is a deliberate one-launch lag: a policy change an admin makes reaches
 * a phone the next time that phone is online and signed in. The alternative —
 * blocking the lock decision on a network call — would mean no lock at all in
 * airplane mode, which is the case it most needs to cover.
 */
import { useEffect } from 'react';

import { useGetMobileAppLockSettingsApiV1UserManagementSettingsMobileAppLockGet } from '../../generated/api/user-management-settings/user-management-settings';
import type { UserManagementMobileAppLockPolicy } from '../../generated/config-enums';
import { rebindRefreshToken } from '../auth/secureTokenStore';
import { useAuth } from '../auth';
import { isAppLockSupportedPlatform, writeCachedPolicy } from './appLockState';

export function AppLockPolicySync() {
  const { isAuthenticated } = useAuth();
  const enabled = isAuthenticated && isAppLockSupportedPlatform();

  const { data } = useGetMobileAppLockSettingsApiV1UserManagementSettingsMobileAppLockGet({
    query: { enabled },
  });

  useEffect(() => {
    if (!enabled || !data) return;
    writeCachedPolicy({
      policy: data.policy as UserManagementMobileAppLockPolicy,
      idleMinutes: data.idle_minutes,
      userPreference: data.user_preference ?? null,
      enabled: data.enabled,
      lockedByPolicy: data.locked_by_policy,
    });
    // Binding follows the lock, so the stored refresh token has to move with it
    // — in BOTH directions, and without waiting for the next rotation. A no-op
    // whenever the stored mode already matches, which is almost always.
    void rebindRefreshToken();
  }, [enabled, data]);

  return null;
}
